import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { forkJoin } from 'rxjs';
import { tap } from 'rxjs/operators';
import { HttpService } from '../../../services/http.service';
import { AUSTRALIA_STATES, requiredTrimmed } from '../../../utils/utils';
import { InputComponent } from '../../from-input/input.component';
import { CustomToastComponent } from '../../toast/custom-toast.component';

interface SchoolType {
    schoolTypeId: string;
    schoolTypeName: string;
    description: string;
    eventHostRegionId: string;
    eventHostRegionName: string;
    isActive: boolean;
}

interface ApiDataResponse {
    status: boolean;
    message: string;
    data: SchoolType[];
}

@Component({
    selector: 'add-school',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, FormsModule, InputComponent, MatDialogModule],
    templateUrl: './add-school.component.html',
    styleUrls: ['./add-school.component.scss']
})
export class AddSchoolComponent implements OnInit, OnDestroy {
    @Input() initialData: any = null;
    @Output() formSubmitted = new EventEmitter<any>();

    australiaStates = AUSTRALIA_STATES.map(s => ({ name: s.value, ...s }));
    form: FormGroup;
    newContactForm: FormGroup;
    newTeacherForm: FormGroup;
    isProcessing: boolean = false;
    isEdit: boolean = false;
    schoolTypeOptions: any = [];
    regionOptions: any = [];
    regionOptionsFull: any = [];
    showNewContactForm: boolean = true;
    showNewTeacherForm: boolean = true;
    isDisabled: boolean = false;
    private dialogConfirmListener?: (event: Event) => void;
    customErrorMessages = {
        postcode: {
            required: 'Please enter a post code.',
            pattern: 'Please enter a 4-digit number.'
        },
    } as const;

    constructor(
        private fb: FormBuilder,
        public dialogRef: MatDialogRef<AddSchoolComponent>,
        private httpService: HttpService,
        private snackBar: MatSnackBar,
        @Inject(MAT_DIALOG_DATA) public data: { initialData: any; EditMode: boolean }
    ) {
        this.isEdit = data?.EditMode || false;
        this.initialData = data?.initialData || null;

        this.form = this.fb.group({
            schoolName: ['', requiredTrimmed],
            schoolType: ['', requiredTrimmed],
            address: ['', requiredTrimmed],
            suburb: ['', requiredTrimmed],
            postcode: ['', [requiredTrimmed, Validators.pattern(/^\d{4}$/)]],
            region: ['', requiredTrimmed],
            state: [{ value: 'Queensland(QLD)', disabled: true }],
            schoolCategory: ['', requiredTrimmed],
            showContacts: [false],
            showTeachers: [false],
            contacts: this.fb.array([]),
            teachers: this.fb.array([])
        });

        this.newContactForm = this.fb.group({
            name: ['', requiredTrimmed],
            phone: [
                '',
                [
                    requiredTrimmed,
                    Validators.pattern(/^(?:0[47]\d{8}|\+61 ?[47]\d{8})$/)
                ]
            ],
            email: ['', [Validators.required, Validators.pattern(
                /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
            )]],
            position: ['', requiredTrimmed]
        });

        this.newTeacherForm = this.fb.group({
            firstName: ['', requiredTrimmed],
            lastName: ['', requiredTrimmed],
            email: ['', [Validators.required, Validators.pattern(
                /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
            )]],
            phone: [
                '',
                [
                    Validators.pattern(/^(?:0[47]\d{8}|\+61 ?[47]\d{8})$/)
                ]
            ]
        });
    }

    ngOnInit(): void {
        this.setupDialogListener();
        this.loadDropdownData();
    }

    ngOnDestroy(): void {
        if (this.dialogConfirmListener) {
            document.removeEventListener('dialogConfirm', this.dialogConfirmListener);
        }
    }

    get contactsArray(): FormArray {
        return this.form.get('contacts') as FormArray;
    }

    get teachersArray(): FormArray {
        return this.form.get('teachers') as FormArray;
    }

    private loadDropdownData(): void {
        forkJoin({
            schoolTypes: this.fetchSchoolsTypeDropDown(),
            regions: this.fetchSchoolsRegionDropDown()
        }).subscribe({
            next: () => {
                if (this.isEdit && this.initialData) {
                    this.patchFormValues();
                    this.loadContactsAndTeachers();
                }
            },
            error: (err) => {
                console.error('Failed to load dropdown data:', err);
                this.showErrorToast('Something went wrong. Please try again later.');
            }
        });
    }

    private patchFormValues(): void {
        let schoolCategory = '';
        if (this.initialData?.isMouSchool) {
            schoolCategory = 'mou';
        } else if (!this.initialData?.isMouSchool) {
            schoolCategory = 'nonMou';
        }

        let regionValue = '';
        if (this.initialData?.region || this.initialData?.regionId || this.initialData?.regionName) {
            const regionToMatch = this.initialData.region || this.initialData.regionName;
            const matchingRegion = this.regionOptionsFull.find(
                (region: any) => region.label === regionToMatch || region.value === this.initialData.regionId
            );

            regionValue = matchingRegion ? matchingRegion.value : '';

            if (!matchingRegion && regionToMatch) {
                const newRegionOption = {
                    label: regionToMatch,
                    value: this.initialData.regionId || this.initialData.region
                };
                this.regionOptions.unshift(newRegionOption);
                this.regionOptionsFull.unshift(newRegionOption);
                regionValue = newRegionOption.value;
            }
        }

        let schoolTypeValue = '';
        if (this.initialData?.schoolType || this.initialData?.schoolTypeId) {
            const schoolTypeToMatch = this.initialData.schoolType || this.initialData.schoolTypeId;
            const matchingSchoolType = this.schoolTypeOptions.find(
                (type: any) => type.value === schoolTypeToMatch
            );

            schoolTypeValue = matchingSchoolType ? matchingSchoolType.value : schoolTypeToMatch;

            if (!matchingSchoolType && this.initialData.schoolTypeName) {
                const newSchoolTypeOption = {
                    label: this.initialData.schoolTypeName,
                    value: this.initialData.schoolTypeId || this.initialData.schoolType
                };
                this.schoolTypeOptions.unshift(newSchoolTypeOption);
                schoolTypeValue = newSchoolTypeOption.value;
            }
        }

        const formData = {
            schoolName: (this.initialData as any)['school name'] ??
                (this.initialData as any).schoolName ??
                this.initialData?.schoolName ?? '',
            schoolType: schoolTypeValue,
            address: this.initialData?.address || '',
            suburb: this.initialData?.suburb || '',
            postcode: this.initialData?.postcode || '',
            region: regionValue,
            state: 'Queensland(QLD)',
            schoolCategory: schoolCategory
        };

        this.form.patchValue(formData);
    }

    private loadContactsAndTeachers(): void {
        if (this.initialData.contacts && this.initialData.contacts.length > 0) {
            this.initialData.contacts.forEach((contact: any) => {
                this.addContactToArray(contact);
            });
        }

        if (this.initialData.teachers && this.initialData.teachers.length > 0) {
            this.initialData.teachers.forEach((teacher: any) => {
                this.addTeacherToArray(teacher);
            });
        }

        this.showNewContactForm = this.contactsArray.length === 0;
        this.showNewTeacherForm = this.teachersArray.length === 0;
        this.form.patchValue({
            showContacts: this.contactsArray.length > 0,
            showTeachers: this.teachersArray.length > 0
        });
    }

    private setupDialogListener(): void {
        this.dialogConfirmListener = () => this.onSubmit();
        document.addEventListener('dialogConfirm', this.dialogConfirmListener);
    }

    fetchSchoolsTypeDropDown() {
        return this.httpService.get<ApiDataResponse>('schooltypes').pipe(
            tap({
                next: (response) => {
                    if (Array.isArray(response.data)) {
                        this.schoolTypeOptions = response.data.map(item => ({
                            label: item.schoolTypeName,
                            value: item.schoolTypeId
                        }));

                        if (this.isEdit && this.initialData?.schoolTypeId) {
                            const exists = this.schoolTypeOptions.find(
                                (option: any) => option.value === this.initialData.schoolTypeId
                            );
                            if (!exists && this.initialData.schoolTypeName) {
                                this.schoolTypeOptions.unshift({
                                    label: this.initialData.schoolTypeName,
                                    value: this.initialData.schoolTypeId
                                });
                            }
                        }
                    } else {
                        this.schoolTypeOptions = [];
                        this.showErrorToast('Invalid school type data format');
                    }
                },
                error: (err) => {
                    this.schoolTypeOptions = [];
                    this.showErrorToast(err?.message || 'Something went wrong. Please try again later.')
                }
            })
        );
    }

    fetchSchoolsRegionDropDown() {
        const type = 2;
        return this.httpService.get<ApiDataResponse>(`eventhostregions?status=${type}`).pipe(
            tap({
                next: (response) => {
                    if (Array.isArray(response.data)) {
                        const activeRegions = response.data.filter(
                            (item: any) => item.status === 'Active'
                        );

                        this.regionOptions = activeRegions.map(item => ({
                            label: item.eventHostRegionName,
                            value: item.eventHostRegionId
                        }));

                        this.regionOptionsFull = response.data.map(item => ({
                            label: item.eventHostRegionName,
                            value: item.eventHostRegionId
                        }));

                        if (this.isEdit && this.data.initialData) {
                            const exists = this.data.initialData
                                ? this.regionOptions.find(
                                    (option: { value: any }) => option.value === this.data.initialData!.regionId
                                )
                                : undefined;
                            if (!exists && this.data.initialData.regionId) {
                                this.regionOptions.unshift({
                                    label: this.data.initialData.region || this.data.initialData.regionName,
                                    value: this.data.initialData.regionId
                                });
                            }
                        }
                    } else {
                        this.regionOptions = [];
                        this.showErrorToast('Invalid region data format');
                    }
                },
                error: (err) => {
                    console.error('Failed to load regions:', err);
                    this.regionOptions = [];
                    this.showErrorToast('Something went wrong. Please try again later.');
                }
            })
        );
    }

    addContact(silent: boolean = false): void {
        if (this.newContactForm.valid && !this.isProcessing) {
            this.addContactToArray(this.newContactForm.value);
            this.newContactForm.reset();
            this.showNewContactForm = false;
            this.newContactForm.markAsPristine();
            if (!silent) {
                this.showSuccessToast('Contact added successfully.');
            }
        } else if (!silent) {
            this.newContactForm.markAllAsTouched();
        }
    }

    cancelContact(): void {
        this.newContactForm.reset();
        this.showNewContactForm = false;
        if (this.contactsArray.length === 0) {
            this.form.patchValue({ showContacts: false });
        }
    }

    cancelTeacher(): void {
        this.newTeacherForm.reset();
        this.showNewTeacherForm = false;
        if (this.teachersArray.length === 0) {
            this.form.patchValue({ showTeachers: false });
        }
    }

    addTeacher(silent: boolean = false): void {
        if (this.newTeacherForm.valid && !this.isProcessing) {
            this.addTeacherToArray(this.newTeacherForm.value);
            this.newTeacherForm.reset();
            this.showNewTeacherForm = false;
            this.newTeacherForm.markAsPristine();
            if (!silent) {
                this.showSuccessToast('Teacher added successfully.');
            }
        } else if (!silent) {
            this.newTeacherForm.markAllAsTouched();
        }
    }

    private addContactToArray(contactData: any): void {
        const contactForm = this.fb.group({
            contactId: [contactData.contactId || null],
            name: [contactData.name, requiredTrimmed],
            phone: [contactData.phone, [
                requiredTrimmed,
                Validators.pattern(/^(?:0[47]\d{8}|\+61 ?[47]\d{8})$/) // FIX: Use same pattern as newContactForm
            ]],
            email: [contactData.email, [Validators.required, Validators.pattern(
                /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
            )]],
            position: [contactData.position, requiredTrimmed]
        });
        this.contactsArray.push(contactForm);
    }

    private addTeacherToArray(teacherData: any): void {
        const teacherForm = this.fb.group({
            firstName: [teacherData.firstName, requiredTrimmed],
            lastName: [teacherData.lastName, requiredTrimmed],
            email: [teacherData.email, [Validators.required, Validators.pattern(
                /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
            )]],
            phone: [teacherData.phone, [
                Validators.pattern(/^(?:0[47]\d{8}|\+61 ?[47]\d{8})$/)
            ]]
        });
        this.teachersArray.push(teacherForm);
    }

    onContactsCheckboxChange(): void {
        const showContacts = this.form.get('showContacts')?.value;
        if (showContacts) {
            this.showNewContactForm = true;
        }
    }

    onTeachersCheckboxChange(): void {
        const showTeachers = this.form.get('showTeachers')?.value;
        if (showTeachers) {
            this.showNewTeacherForm = true;
        }
    }

    deleteContact(index: number): void {
        if (!this.isProcessing) {
            this.contactsArray.removeAt(index);
            if (this.contactsArray.length === 0) {
                this.showNewContactForm = true;
                this.form.patchValue({ showContacts: false });
            }
            this.showSuccessToast('Contact deleted successfully.');
        }
    }

    deleteTeacher(index: number): void {
        if (!this.isProcessing) {
            this.teachersArray.removeAt(index);
            if (this.teachersArray.length === 0) {
                this.showNewTeacherForm = true;
                this.form.patchValue({ showTeachers: false });
            }
            this.showSuccessToast('Teacher deleted successfully.');
        }
    }

    editContact(index: number): void {
        if (!this.isProcessing) {
            const contact = this.contactsArray.at(index) as FormGroup;
            this.newContactForm.patchValue(contact.value);
            this.contactsArray.removeAt(index);
            this.showNewContactForm = true;
        }
    }

    editTeacher(index: number): void {
        if (!this.isProcessing) {
            const teacher = this.teachersArray.at(index) as FormGroup;
            this.newTeacherForm.patchValue(teacher.value);
            this.teachersArray.removeAt(index);
            this.showNewTeacherForm = true;
        }
    }

    onSubmit(): void {
        if (this.isProcessing) return;
        this.form.markAllAsTouched();
        this.validateFormArrays();
        if (this.showNewContactForm && this.form.get('showContacts')?.value) {
            this.newContactForm.markAllAsTouched();
            if (!this.newContactForm.valid) {
                this.showErrorToast('Please fill all required contact fields correctly.');
                return;
            }
            this.addContact(true);
        }
        if (this.showNewTeacherForm && this.form.get('showTeachers')?.value) {
            this.newTeacherForm.markAllAsTouched();
            if (!this.newTeacherForm.valid) {
                this.showErrorToast('Please fill all required teacher fields correctly.');
                return;
            }
            this.addTeacher(true);
        }

        if (this.form.valid) {
            this.isProcessing = true;
            this.isDisabled = true;
            const schoolCategory = this.form.value.schoolCategory;
            const isMouSchool = schoolCategory === 'mou';
            const isNonMouSchool = schoolCategory === 'nonMou';
            const selectedRegion = this.regionOptionsFull.find(
                (region: any) => region.value === this.form.value.region
            );
            const schoolData = {
                schoolName: this.form.value.schoolName?.trim(),
                isMouSchool: isMouSchool,
                isNonMouSchool: isNonMouSchool,
                schoolTypeId: this.form.value.schoolType,
                address: this.form.value.address?.trim(),
                suburb: this.form.value.suburb?.trim(),
                postcode: this.form.value.postcode,
                state: this.form.getRawValue().state,
                region: selectedRegion ? selectedRegion.label : this.form.value.region,
                regionId: this.form.value.region,
                contacts: this.form.value.contacts || [],
                teachers: this.form.value.teachers.map((teacher: any) => ({
                    firstName: teacher.firstName?.trim(),
                    lastName: teacher.lastName?.trim(),
                    email: teacher.email?.trim(),
                    mobileNumber: teacher.phone
                })) || []
            };


            const request = this.isEdit
                ? this.httpService.put<any>(`schools/${this.initialData?.id || this.initialData?.schoolId}`, schoolData)
                : this.httpService.post<any>('schools', schoolData);

            request.subscribe({
                next: (response: any) => {
                    console.log('API response:', response);
                    if (response.status) {
                        this.formSubmitted.emit({
                            ...schoolData,
                            schoolId: response.data?.schoolId || this.initialData?.schoolId
                        });
                        const message = this.isEdit
                            ? response.message || 'School updated successfully.'
                            : response.message || 'School created successfully.';
                        this.showSuccessToast(message);
                        this.isProcessing = false;
                        this.isDisabled = false;
                        this.dialogRef.close({ success: true, data: response.data });
                    } else {
                        const message = response.message || `Failed to ${this.isEdit ? 'update' : 'create'} school.`;
                        this.showErrorToast(message);
                        this.isProcessing = false;
                        this.isDisabled = false;
                    }
                },
                error: (err: any) => {
                    const message =
                        err?.errors?.general ||
                        err?.errors?.General ||
                        err?.errors?.message ||
                        err?.message ||
                        'Something went wrong. Please try again later.';
                    this.showErrorToast(message);
                    this.isProcessing = false;
                    this.isDisabled = false;
                },
                complete: () => {
                    console.log('API request completed');
                    this.isProcessing = false;
                    this.isDisabled = false;
                }
            });
        } else {
            console.log('Form is invalid');
            this.showFormValidationErrors();
        }
    }

    private validateFormArrays(): void {
        this.contactsArray.controls.forEach((contactGroup: any, index: number) => {
            contactGroup.markAllAsTouched();
            if (contactGroup.invalid) {
                console.log(`Contact at index ${index} is invalid:`, contactGroup.errors);
            }
        });
        this.teachersArray.controls.forEach((teacherGroup: any, index: number) => {
            teacherGroup.markAllAsTouched();
            if (teacherGroup.invalid) {
                console.log(`Teacher at index ${index} is invalid:`, teacherGroup.errors);
                Object.keys(teacherGroup.controls).forEach(controlName => {
                    const control = teacherGroup.get(controlName);
                    if (control?.invalid) {
                        console.log(`  ${controlName} errors:`, control.errors);
                    }
                });
            }
        });
    }

    private showFormValidationErrors(): void {
        console.log('Form validation errors check');
        const invalidControls = this.getInvalidControls(this.form);
        if (invalidControls.length > 0) {
            console.log('Invalid form controls:', invalidControls);
            const firstInvalid = invalidControls[0];
            const fieldNameMap: { [key: string]: string } = {
                'schoolName': 'School Name',
                'schoolType': 'School Type',
                'address': 'Address',
                'suburb': 'Suburb',
                'postcode': 'Postcode',
                'region': 'Region',
                'state': 'State',
                'schoolCategory': 'School Category'
            };
            const fieldName = fieldNameMap[firstInvalid.control] || firstInvalid.control;

            let errorMessage = '';
            if (firstInvalid.errors.includes('required')) {
                errorMessage = `${fieldName} is required.`;
            } else if (firstInvalid.errors.includes('pattern')) {
                if (firstInvalid.control === 'postcode') {
                    errorMessage = 'Please enter a valid 4-digit postcode.';
                } else {
                    errorMessage = `${fieldName} has an invalid format.`;
                }
            } else {
                errorMessage = `${fieldName} is invalid.`;
            }

            this.showErrorToast(errorMessage);
            return;
        }
        if (!this.contactsArray.valid) {
            console.log('Contacts array invalid');
            this.showErrorToast('Please fix contact information errors.');
            return;
        }

        if (!this.teachersArray.valid) {
            console.log('Teachers array invalid');
            this.teachersArray.controls.forEach((teacherGroup: any, index: number) => {
                if (teacherGroup.invalid) {
                    console.log(`Teacher ${index + 1} has errors:`, teacherGroup.errors);
                    Object.keys(teacherGroup.controls).forEach(controlName => {
                        const control = teacherGroup.get(controlName);
                        if (control?.invalid) {
                            console.log(`  ${controlName}:`, control.errors);
                            if (control.errors?.['pattern']) {
                                this.showErrorToast(`Teacher ${index + 1} phone number is invalid. Please use format: 04XXXXXXXX or +61 4XXXXXXXX`);
                                return;
                            }
                        }
                    });
                }
            });
            this.showErrorToast('Please fix teacher information errors.');
            return;
        }
        if (this.form.get('showContacts')?.value && this.contactsArray.length === 0) {
            this.showErrorToast('Please add at least one contact or uncheck "Contact details".');
            return;
        }

        if (this.form.get('showTeachers')?.value && this.teachersArray.length === 0) {
            this.showErrorToast('Please add at least one teacher or uncheck "Teacher details".');
            return;
        }

        this.showErrorToast('Please check all required fields and try again.');
    }

    private getInvalidControls(form: FormGroup): { control: string; errors: string[] }[] {
        const invalidList: { control: string; errors: string[] }[] = [];
        Object.keys(form.controls).forEach(key => {
            const control = form.get(key);
            if (!control) return;
            if (key === 'contacts' || key === 'teachers' || key === 'showContacts' || key === 'showTeachers') {
                return;
            }
            if (control.invalid) {
                const errors = Object.keys(control.errors || {});
                invalidList.push({
                    control: key,
                    errors
                });
            }
        });

        return invalidList;
    }

    onCancel(): void {
        if (this.isProcessing) return;
        this.dialogRef.close(false);
    }

    private showSuccessToast(message: string): void {
        this.snackBar.openFromComponent(CustomToastComponent, {
            data: { message },
            duration: 3000,
            horizontalPosition: 'right',
            verticalPosition: 'top',
            panelClass: ['no-padding-snackbar', 'success-toast']
        });
    }

    private showErrorToast(message: string | null | undefined): void {
        if (!message) return;
        this.snackBar.openFromComponent(CustomToastComponent, {
            data: { message },
            duration: 3000,
            horizontalPosition: 'right',
            verticalPosition: 'top',
            panelClass: ['no-padding-snackbar', 'error-toast']
        });
    }

    restrictToNumbers(event: KeyboardEvent, controlName: string, form: FormGroup): void {
        const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'];
        if (!allowedKeys.includes(event.key) && !/^[0-9]$/.test(event.key)) {
            event.preventDefault();
        }
    }
}